import lamejs from '@breezystack/lamejs';

import { remuxWebmToOgg } from './webmOpusToOgg';

const writeString = (view, offset, string) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const bufferToWav = async (buffer, numChannels, sampleRate) => {
  const length = buffer.length * numChannels * 2;
  const wav = new ArrayBuffer(44 + length);
  const view = new DataView(wav);

  // WAV Header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length, true);

  // WAV Data
  const offset = 44;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < buffer.length; i++) {
    // eslint-disable-next-line no-plusplus
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(
        -1,
        Math.min(1, buffer.getChannelData(channel)[i])
      );
      view.setInt16(
        offset + (i * numChannels + channel) * 2,
        sample * 0x7fff,
        true
      );
    }
  }

  return new Blob([wav], { type: 'audio/wav' });
};

const decodeAudioData = async audioBlob => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await audioBlob.arrayBuffer();
  const audioData = await audioContext.decodeAudioData(arrayBuffer);
  return audioData;
};

export const convertToWav = async audioBlob => {
  const audioBuffer = await decodeAudioData(audioBlob);
  return bufferToWav(
    audioBuffer,
    audioBuffer.numberOfChannels,
    audioBuffer.sampleRate
  );
};

/**
 * Encodes audio samples to MP3 format.
 * @param {number} channels - 1 (mono) or 2 (stereo).
 * @param {number} sampleRate - Sample rate in Hz.
 * @param {Int16Array[]} channelSamples - One Int16Array per channel.
 * @param {number} bitrate - MP3 bitrate (default: 128)
 * @returns {Blob} - The MP3 encoded audio as a Blob.
 */
export const encodeToMP3 = (
  channels,
  sampleRate,
  channelSamples,
  bitrate = 128
) => {
  const outputBuffer = [];
  const encoder = new lamejs.Mp3Encoder(channels, sampleRate, bitrate);
  const maxSamplesPerFrame = 1152;
  const totalSamples = channelSamples[0].length;

  for (let offset = 0; offset < totalSamples; offset += maxSamplesPerFrame) {
    const sliceEnd = Math.min(offset + maxSamplesPerFrame, totalSamples);
    const left = channelSamples[0].subarray(offset, sliceEnd);
    // lamejs en estéreo exige buffers IZQUIERDO y DERECHO separados; pasar un
    // único buffer entrelazado (comportamiento anterior) corrompía o rompía
    // la codificación con micrófonos estéreo.
    const mp3Buffer =
      channels === 2
        ? encoder.encodeBuffer(
            left,
            channelSamples[1].subarray(offset, sliceEnd)
          )
        : encoder.encodeBuffer(left);

    if (mp3Buffer.length > 0) {
      outputBuffer.push(new Int8Array(mp3Buffer));
    }
  }

  const remainingData = encoder.flush();
  if (remainingData.length > 0) {
    outputBuffer.push(new Int8Array(remainingData));
  }

  return new Blob(outputBuffer, { type: 'audio/mp3' });
};

/**
 * Converts an audio Blob to an MP3 format Blob.
 * Las notas de voz se downmixean SIEMPRE a mono: lamejs solo soporta 1-2
 * canales y ciertos equipos (micrófonos estéreo, arrays multicanal, headsets
 * Bluetooth) producían audio que rompía la codificación. Mono elimina esos
 * modos de fallo y genera archivos más pequeños.
 * @param {Blob} audioBlob - The audio data as a Blob.
 * @param {number} bitrate - MP3 bitrate (default: 128)
 * @returns {Promise<Blob>} - A Blob containing the MP3 encoded audio.
 */
export const convertToMp3 = async (audioBlob, bitrate = 128) => {
  try {
    const audioBuffer = await decodeAudioData(audioBlob);
    const { numberOfChannels, length, sampleRate } = audioBuffer;
    // eslint-disable-next-line no-console
    console.log(
      `[mp3ConversionUtils] grabación: canales=${numberOfChannels} sampleRate=${sampleRate} duración=${(length / sampleRate).toFixed(1)}s`
    );

    const channelData = [];
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      channelData.push(audioBuffer.getChannelData(channel));
    }

    const mono = new Int16Array(length);
    for (let i = 0; i < length; i += 1) {
      let sum = 0;
      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        sum += channelData[channel][i];
      }
      const sample = Math.max(-1, Math.min(1, sum / numberOfChannels));
      mono[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }

    return encodeToMP3(1, sampleRate, [mono], bitrate);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('[mp3ConversionUtils] fallo convirtiendo a MP3:', error);
    throw new Error(`Conversion to MP3 failed: ${error.message}`);
  }
};

export const convertAudio = async (inputBlob, outputFormat, bitrate = 128) => {
  let audio;
  if (outputFormat === 'audio/wav') {
    audio = await convertToWav(inputBlob);
  } else if (outputFormat === 'audio/mp3') {
    audio = await convertToMp3(inputBlob, bitrate);
  } else if (outputFormat === 'audio/ogg') {
    const inputType = inputBlob.type.split(';')[0].trim();
    if (inputType === 'audio/webm' || inputType === 'video/webm') {
      audio = await remuxWebmToOgg(inputBlob);
    } else if (inputType === 'audio/ogg') {
      audio = inputBlob;
    } else {
      // Browsers that record neither WebM nor OGG (e.g. Safari records
      // audio/mp4) cannot produce OGG/Opus. Fall back to MP3 so the recording
      // still sends as a regular audio message instead of failing. The caller
      // keys the voice-note flag off the returned blob type, so an MP3 result
      // is never mislabeled as an OGG/Opus voice note.
      audio = await convertToMp3(inputBlob, bitrate);
    }
  } else {
    throw new Error('Unsupported output format');
  }
  return audio;
};
